/**
 * DisplayHeader Component
 * Shows clinic name, date and time for TV display
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { DarkMode as DarkModeIcon, LightMode as LightModeIcon } from '@mui/icons-material';

interface DisplayHeaderProps {
  clinicName?: string;
  onToggleTheme?: () => void;
  themeMode?: 'light' | 'dark';
}

/**
 * Header component for display panel
 * Shows clinic name and auto-updating clock
 */
export const DisplayHeader: React.FC<DisplayHeaderProps> = ({ clinicName, onToggleTheme, themeMode }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        pb: 1,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Clinic Name */}
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          textTransform: 'uppercase',
          letterSpacing: 2,
        }}
      >
        {clinicName || 'Painel de Chamadas'}
      </Typography>

      {/* Theme Toggle + Date and Time */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {onToggleTheme && (
          <Tooltip title={themeMode === 'dark' ? 'Tema Claro' : 'Tema Escuro'}>
            <IconButton onClick={onToggleTheme} sx={{ color: 'text.secondary' }}>
              {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        )}
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            fontFamily: 'monospace',
          }}
        >
          {formatTime(currentTime)}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            textTransform: 'capitalize',
          }}
        >
          {formatDate(currentTime)}
        </Typography>
      </Box>
      </Box>
    </Box>
  );
};

export default DisplayHeader;
